import { pacedStateSchema, publicSessionSchema, type Answer, type PacedState, type PublicSession } from "@engage/contracts";
import type { RuntimeConfig } from "./config";

/**
 * The participant's HTTP calls. In a live session everything after joining — answering,
 * asking, voting — goes over the broker; a self-paced one (ADR 0008) is all HTTP, since
 * nothing in it is broadcast. Either way the server times every answer (Rule 2).
 */

/** The API refused because this network has tried too many codes (HTTP 429). */
export class TooManyAttempts extends Error {}

/** The session has admitted as many people as its organisation's plan allows (HTTP 409). */
export class SessionFull extends Error {}

/** The name won't be shown on screen: the server's filter refused it (HTTP 422). */
export class NameRefused extends Error {}

/** Is this code open, and what does the session want of us? Null if not found. */
export async function lookup(cfg: RuntimeConfig, code: string): Promise<PublicSession | null> {
  const res = await fetch(`${cfg.apiUrl}/join/${encodeURIComponent(code)}`);
  if (res.status === 404) return null;
  if (res.status === 429) throw new TooManyAttempts();
  if (!res.ok) throw new Error(`lookup: ${res.status}`);
  return publicSessionSchema.parse(await res.json());
}

export interface Joined {
  code: string;
  participantId: string;
  /** The credential for every reconnect. Returned once by the server; kept on this device. */
  rejoinToken: string;
  /** Shown at the end, to prove a prize is theirs. Absent from a join saved before codes existed. */
  claimCode?: string | null;
  displayName: string | null;
  session: PublicSession;
}

export async function join(cfg: RuntimeConfig, code: string, displayName?: string): Promise<Joined | null> {
  const res = await fetch(`${cfg.apiUrl}/join`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ joinCode: code, ...(displayName ? { displayName } : {}) }),
  });
  if (res.status === 404) return null;
  if (res.status === 409) throw new SessionFull();
  if (res.status === 422) throw new NameRefused();
  if (res.status === 429) throw new TooManyAttempts();
  if (!res.ok) throw new Error(`join: ${res.status}`);
  const body = (await res.json()) as { participantId: string; rejoinToken: string; claimCode?: string; session: unknown };
  return {
    code,
    participantId: body.participantId,
    rejoinToken: body.rejoinToken,
    claimCode: body.claimCode ?? null,
    displayName: displayName ?? null,
    session: publicSessionSchema.parse(body.session),
  };
}

/** A self-paced session that has closed, or a place in it this device no longer holds. */
export class SessionClosed extends Error {}

function participantHeaders(j: Joined): Record<string, string> {
  return { "content-type": "application/json", "x-join-code": j.code, authorization: `Bearer ${j.rejoinToken}` };
}

/** Where this person is in a self-paced session; the server starts the next slide's clock if none is open. */
export async function pacedCurrent(cfg: RuntimeConfig, j: Joined): Promise<PacedState> {
  const res = await fetch(`${cfg.apiUrl}/paced/current`, { method: "POST", headers: participantHeaders(j), body: "{}" });
  if (res.status === 401 || res.status === 410) throw new SessionClosed();
  if (!res.ok) throw new Error(`paced: ${res.status}`);
  return pacedStateSchema.parse(await res.json());
}

/** "late": past the question's time. "again": already answered. A string: why it was refused. */
export async function pacedAnswer(
  cfg: RuntimeConfig,
  j: Joined,
  slideId: string,
  answer: Answer,
): Promise<"saved" | "late" | "again" | { refused: string }> {
  const res = await fetch(`${cfg.apiUrl}/paced/answer`, {
    method: "POST",
    headers: participantHeaders(j),
    body: JSON.stringify({ slideId, answer }),
  });
  if (res.ok) return "saved";
  if (res.status === 401) throw new SessionClosed();
  if (res.status === 410) return "late";
  if (res.status === 409) return "again";
  if (res.status === 422) return { refused: ((await res.json()) as { message?: string }).message ?? "" };
  throw new Error(`paced answer: ${res.status}`);
}

export async function pacedSkip(cfg: RuntimeConfig, j: Joined, slideId: string): Promise<void> {
  const res = await fetch(`${cfg.apiUrl}/paced/skip`, {
    method: "POST",
    headers: participantHeaders(j),
    body: JSON.stringify({ slideId }),
  });
  if (res.status === 401 || res.status === 410) throw new SessionClosed();
  // 409 is a slide already moved on from (a second tab): the next fetch shows where they are.
  if (!res.ok && res.status !== 409) throw new Error(`paced skip: ${res.status}`);
}
